import {OAuthService} from 'angular-oauth2-oidc';
import {BehaviorSubject, from, Observable, of, takeWhile} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {AbstractSubscribable} from './abstract-subscribable';
import {authConfig} from './auth.config';
import {Router} from '@angular/router';

export function authServiceFactory(oauthService: OAuthService, router: Router): AuthService {
  return environment.sso.enabled
    ? new DefaultAuthService(oauthService, router)
    : new LocalAuthService();
}

export abstract class AuthService {
  abstract login: (targetUrl?: string) => Observable<boolean>;
}

export class DefaultAuthService
  extends AbstractSubscribable
  implements AuthService
{
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);

  private isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);

  constructor(private readonly oauthService: OAuthService, private readonly router: Router) {
    super();
    this.oauthService.events.subscribe(() =>
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken())
    );

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public login(targetUrl?: string): Observable<boolean> {
    return from(this.runInitialLoginSequence(targetUrl)).pipe(
      takeWhile(() => this.subscribe),
      switchMap(() => this.isAuthenticated$),
      tap(
        (isAuthenticated) =>
          isAuthenticated || this.oauthService.initLoginFlow(targetUrl)
      )
    );
  }

  private runInitialLoginSequence(targetUrl?: string): Promise<void> {
    return this.oauthService
      .loadDiscoveryDocument(
        `${authConfig.issuer}/${environment.sso.discoveryDocumentEndpoint}`
      )
      .then(() => this.oauthService.tryLogin())
      .then(() => {
        if (this.oauthService.hasValidAccessToken()) {
          const urlToNavigate = this.resolveStateUrl(this.oauthService.state) || targetUrl;
          if (urlToNavigate && urlToNavigate !== '/' && urlToNavigate !== '') {
            this.router.navigateByUrl(urlToNavigate);
          }
        }
        this.isDoneLoadingSubject$.next(true);
        return Promise.resolve();
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  private resolveStateUrl(stateUrl: string | undefined): string | undefined {
    if (!stateUrl) return undefined;

    // Handle state format with separator: randomString;/target/url
    if (stateUrl.includes(';')) {
      const parts = stateUrl.split(';');
      stateUrl = parts.length > 1 ? parts[1] : stateUrl;
    }

    try {
      let decoded = decodeURIComponent(stateUrl);
      // If still encoded, decode again
      if (decoded.includes('%')) decoded = decodeURIComponent(decoded);
      return decoded;
    } catch (e) {
      console.warn('Failed to decode state URL:', e);
      return stateUrl;
    }
  }
}

class LocalAuthService implements AuthService {
  login(): Observable<boolean> {
    localStorage.setItem('key', 'my-key');
    return of(true);
  }
}
